import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { useTranslation } from 'react-i18next';
import RecommendedLawyerItem from './LawyerRecommendation';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { fetchMatterSummary } from '../../store/actions/Matters/MatterActions';
import { findLawyers, getRecommendedLawyers } from '../../store/actions/Finders/FIlnderActions';

interface LawyerRecommendation {
  lawyer_id: string;
  first_name: string;
  last_name: string;
  specialty: string[];
  experience: number;
  hourly_rate: number;
  location: string[] | string;
  ratings_average: number;
  reason: string;
  profilePicture?: string;
  bio?: string;
}

const FinderCasePage: React.FC = () => {
  const { t } = useTranslation();
  const { caseId } = useParams<{ caseId: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [recommendations, setRecommendations] = useState<LawyerRecommendation[]>([]);
  const [caseName, setCaseName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCaseData = async () => {
      try {
        const caseSummary = await dispatch(fetchMatterSummary(caseId));
        const name = caseSummary.summary.CaseName || `Case ${caseId}`;
        setCaseName(name);
        setBreadcrumbs([
          { name: t('Finder'), href: '/finder', current: false },
          { name, href: `/finder/${caseId}`, current: true },
        ]);
      } catch (err) {
        console.error('Error fetching case summary:', err);
        setCaseName(`${caseName}`);
        setBreadcrumbs([
          { name: t('Finder'), href: '/finder', current: false },
          { name: `${caseName}`, href: `/finder/${caseId}`, current: true },
        ]);
      }
    };

    const fetchRecommendations = async () => {
      try {
        const recommendedLawyers = await dispatch(getRecommendedLawyers(caseId));
        setRecommendations(recommendedLawyers);
      } catch (err) {
        console.error('Error fetching recommendations:', err);
        setError(t('Failed to load recommendations.'));
      } finally {
        setLoading(false);
      }
    };

    fetchCaseData();
    fetchRecommendations();
  }, [caseId, dispatch, t, setBreadcrumbs]);

  const handleFindLawyers = async () => {
    try {
      await dispatch(findLawyers(caseId, { budget: '', timeframe: '' }));
      const updatedRecommendations = await dispatch(getRecommendedLawyers(caseId));
      setRecommendations(updatedRecommendations);
    } catch (err) {
      console.error('Error finding lawyers:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <Header
        actions={[
          { label: t('Find lawyers'), onClick: handleFindLawyers, variant: 'primary' },
        ]}
      />
      <FilterBarWithTabs
        onSearchChange={() => {}} 
      />
      <div className="p-6">
        {loading ? (
          <Skeleton variant="grid" />
        ) : error ? (
          <p className="text-danger-500">{error}</p>
        ) : recommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendations.map((lawyer) => (
              <RecommendedLawyerItem
                key={lawyer.lawyer_id}
                lawyer={lawyer}
                onClick={() => history.push({
                  pathname: `/finder/${caseId}/${lawyer.lawyer_id}`,
                  state: { lawyer, caseName, caseId },
                })}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-400">
            {t('No recommendations found. Click "Find Lawyers" to get started.')}
          </p>
        )}
      </div>
    </div>
  );
};

export default FinderCasePage;